/**
 * Shared bucket helper for /cases responses.
 *
 * Used by labeling study views and SegStudyHome to render two-tone
 * progress bars (completed | in-progress | unlabeled) so the two views
 * can't drift on which statuses count toward which segment.
 */
import type {
  CaseStatus,
  CasesListResponse,
  ProjectStatsResponse,
} from "../hooks/useLabelingQueries";

/** Why the segmentation study locks a case's cards, or null when editable. */
export type CaseLockReason = "complete" | "unassessable";

/** Complete locks the case; unassessable locks it only for the reader's own
 *  flag, because only that reader sees "Clear my flag" to unlock it. */
export function caseLockReason(
  status: CaseStatus | undefined,
  callerEmail: string | undefined,
): CaseLockReason | null {
  if (status?.completion_status === "complete") return "complete";
  if (!callerEmail) return null;
  const ownFlag = status?.unassessable_dispositions?.some(
    (item) => item.disposition === "unassessable" && item.recorded_by.toLowerCase() === callerEmail,
  );
  return ownFlag ? "unassessable" : null;
}

export interface CaseBucket {
  completed: number;
  inProgress: number;
  total: number;
}

/** Bucket from a /cases response — iterates per-case completion_status.
 *  Use only when the caller already needs the case rows (e.g. the
 *  details popover); otherwise prefer bucketStats to avoid pulling
 *  the full case list just to render a progress bar. */
export function bucketCases(data: CasesListResponse | undefined): CaseBucket {
  let completed = 0;
  let inProgress = 0;
  for (const c of data?.cases ?? []) {
    if (c.completion_status === "complete") completed++;
    else if (c.completion_status === "partial") inProgress++;
  }
  return { completed, inProgress, total: data?.total_cases ?? 0 };
}

/** Bucket from a /stats response — uses the aggregate counts the
 *  backend already computes (`completed_cases`, `in_progress_cases`,
 *  `total_cases`). One small payload per project instead of the full
 *  case list. */
export function bucketStats(data: ProjectStatsResponse | undefined): CaseBucket {
  return {
    completed: data?.completed_cases ?? 0,
    inProgress: data?.in_progress_cases ?? 0,
    total: data?.total_cases ?? 0,
  };
}
